/**
 * Session review detail: status/progress/token/cost/duration, live tail refresh,
 * M1.1 control skeleton (default off) and the event timeline (rendered directly;
 * masking was removed in task-31). Read-only unless an explicit signed control
 * command is confirmed in the panel.
 */
import { useCallback, useEffect, useReducer, useState } from 'react';
import type { SessionEventView, SessionView } from '../lib/types';
import {
  SESSION_STATUS_LABELS,
  eventKey,
  mergeSessionEvents,
  sessionDurationSeconds,
  sessionKey,
  sessionProgress,
  sessionStartedAt,
} from '../lib/sessions';
import {
  INITIAL_SESSION_HISTORY_STATE,
  earliestSessionEventTs,
  mergeHistoryEvents,
  reduceSessionHistory,
} from '../lib/session-history';
import {
  formatClock,
  formatCost,
  formatDurationSeconds,
  formatRelative,
  formatTokens,
} from '../lib/format';
import { nowSeconds } from '../lib/id';
import {
  SESSION_MONITORING_CONFIRM_ACTION,
  SESSION_MONITORING_CONFIRM_LINES,
  SESSION_MONITORING_CONFIRM_TITLE,
  SESSION_MONITORING_MENU_LABEL,
  SESSION_MONITORING_PENDING_COPY,
  sessionExclusionReasonLabel,
} from '../lib/session-monitoring';
import { useApp } from '../state/AppState';
import { ConfirmDialog } from './ConfirmDialog';
import { E2ENote } from './E2ENote';
import { Icon } from './Icon';
import { MaskedText } from './MaskedText';
import { SessionControlPanel } from './SessionControlPanel';
import { EventKindBadge, SourceBadge, StatusBadge } from './SessionBadges';
import { SessionProgress } from './SessionProgress';
import { StatusBanner } from './StatusBanner';

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.03] px-2.5 py-2">
      <p className="text-[10px] text-slate-500">{label}</p>
      <p className="mt-0.5 truncate font-mono text-xs text-slate-100">{value}</p>
    </div>
  );
}

export function SessionDetail({
  session,
  now,
  live,
  onLiveChange,
  onBack,
}: {
  session: SessionView;
  now: number;
  live: boolean;
  onLiveChange(live: boolean): void;
  onBack(): void;
}) {
  const {
    sessionTail,
    refreshSessions,
    settings,
    requestSessionHistory,
    sessionExclusions,
    ignoreSession,
    unignoreSession,
    paired,
    network,
    relayPlan,
  } = useApp();
  const [events, setEvents] = useState<SessionEventView[]>(() =>
    mergeSessionEvents([], session.events),
  );
  const [lastTailAt, setLastTailAt] = useState<number | null>(null);
  const [history, dispatchHistory] = useReducer(
    reduceSessionHistory,
    INITIAL_SESSION_HISTORY_STATE,
  );
  /** v0.5.0 monitoring menu: “不再监视此会话” lives in the detail “⋯” menu. */
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmingIgnore, setConfirmingIgnore] = useState(false);
  const [monitoringBusy, setMonitoringBusy] = useState(false);
  const [monitoringError, setMonitoringError] = useState<string | null>(null);
  const exclusion = sessionExclusions.find(
    (entry) => entry.source === session.source && entry.session_id === session.session_id,
  );
  /**
   * Monitoring commands reuse the signed relay path, so the same device/relay
   * gates as session control apply. Demo mode stays local/mock only (shown as a
   * fixed reason instead of a command that can never be signed).
   */
  const monitoringUnavailable = settings.demoMode
    ? '演示模式不发送监视命令'
    : !paired
      ? '尚未完成配对'
      : !network.online
        ? '网络离线'
        : !relayPlan.enabled || !network.relayConnected
          ? 'live relay 未就绪'
          : null;

  // Reset the timeline/history state when a different session is opened.
  useEffect(() => {
    setEvents(mergeSessionEvents([], session.events));
    setLastTailAt(null);
    dispatchHistory({ type: 'reset' });
  }, [session.source, session.session_id]);

  // Fold in fresh snapshots coming from the list refresh.
  useEffect(() => {
    setEvents((previous) => mergeSessionEvents(previous, session.events));
  }, [session.events]);

  const refresh = useCallback(async (): Promise<void> => {
    const incoming = await sessionTail(session.source, session.session_id);
    setEvents((previous) => mergeSessionEvents(previous, incoming));
    setLastTailAt(nowSeconds());
    if (settings.demoMode) refreshSessions();
  }, [sessionTail, session.source, session.session_id, settings.demoMode, refreshSessions]);

  /**
   * Pull one older page (v0.3.0). History is merged into the same timeline with
   * the shared dedupe/order rules; realtime refresh keeps running. One request at
   * a time, and a failed page is retryable without dropping existing events.
   */
  const loadEarlier = useCallback(async (): Promise<void> => {
    if (history.loading || history.exhausted) return;
    dispatchHistory({ type: 'start', now: nowSeconds() });
    // First page: anchor reverse pagination at the currently earliest displayed
    // event (`before_ts`, no cursor). Later pages pass the opaque cursor back.
    const beforeTs = earliestSessionEventTs(events);
    const result = await requestSessionHistory(
      session,
      history.nextCursor !== undefined
        ? { cursor: history.nextCursor }
        : beforeTs !== undefined
          ? { beforeTs }
          : {},
    );
    if (result.ok) {
      setEvents((previous) => mergeHistoryEvents(previous, result.events));
      dispatchHistory({
        type: 'success',
        ...(result.nextCursor !== undefined ? { nextCursor: result.nextCursor } : {}),
        now: nowSeconds(),
      });
    } else {
      dispatchHistory({ type: 'error', error: result.error, now: nowSeconds() });
    }
  }, [
    history.loading,
    history.exhausted,
    history.nextCursor,
    requestSessionHistory,
    session,
    events,
  ]);

  // Live tail: only running sessions grow; static sessions keep their snapshot.
  useEffect(() => {
    if (!live || session.status !== 'active') return undefined;
    const poll = (): void => {
      void refresh().catch(() => undefined);
    };
    poll();
    const timer = setInterval(poll, 2500);
    return () => clearInterval(timer);
  }, [live, session.status, refresh]);

  const progress = sessionProgress(session);
  const duration = sessionDurationSeconds(session, now);
  const startedAt = sessionStartedAt(session);

  /** “不再监视此会话”: signed ignore; on success the list hides it. */
  const confirmIgnore = async (): Promise<void> => {
    if (monitoringBusy) return;
    setMonitoringBusy(true);
    setMonitoringError(null);
    const result = await ignoreSession(session);
    setMonitoringBusy(false);
    if (result.ok) {
      setConfirmingIgnore(false);
      setMenuOpen(false);
      // Leave the detail: the session is now filtered out of the list.
      onBack();
      return;
    }
    setMonitoringError(result.error ?? '停止监视请求失败');
  };

  /** Restore from the same menu (e.g. a stale local entry still rendered). */
  const resumeMonitoring = async (): Promise<void> => {
    if (!exclusion || monitoringBusy) return;
    setMonitoringBusy(true);
    setMonitoringError(null);
    const result = await unignoreSession(exclusion);
    setMonitoringBusy(false);
    if (result.ok) {
      setMenuOpen(false);
      return;
    }
    setMonitoringError(result.error ?? '恢复监视请求失败');
  };

  return (
    <div className="flex h-full flex-col">
      <StatusBanner />
      <div className="flex items-center justify-between gap-2 px-4 pb-2">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-300"
        >
          <Icon name="back" className="h-3.5 w-3.5" />
          返回
        </button>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onLiveChange(!live)}
            aria-pressed={live}
            className={`flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-[11px] ${
              live
                ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200'
                : 'border-white/10 bg-white/5 text-slate-400'
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${live ? 'animate-pulse bg-emerald-400' : 'bg-slate-500'}`}
            />
            {live ? '实时刷新' : '已暂停'}
          </button>
          <button
            type="button"
            onClick={refresh}
            className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1.5 text-[11px] text-slate-300"
          >
            <Icon name="refresh" className="h-3.5 w-3.5" />
            刷新
          </button>
          <div className="relative">
            <button
              type="button"
              data-testid="session-monitoring-menu-button"
              aria-label="更多会话操作"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => {
                setMenuOpen((value) => !value);
                setMonitoringError(null);
              }}
              className="flex h-[30px] w-[30px] items-center justify-center rounded-full border border-white/10 bg-white/5 text-sm leading-none text-slate-300"
            >
              ⋯
            </button>
            {menuOpen ? (
              <div
                role="menu"
                aria-label="会话操作"
                className="absolute right-0 top-full z-30 mt-1 w-60 rounded-xl border border-white/10 bg-[#0f1629] p-2 shadow-2xl"
              >
                {exclusion ? (
                  <>
                    <p className="px-1 pb-1 text-[10px] text-amber-200/90">
                      已停止监视（{sessionExclusionReasonLabel(exclusion)}）
                    </p>
                    <button
                      type="button"
                      role="menuitem"
                      data-testid="session-monitoring-resume"
                      disabled={
                        monitoringBusy ||
                        exclusion.pending === true ||
                        monitoringUnavailable !== null
                      }
                      onClick={() => void resumeMonitoring()}
                      className="w-full rounded-lg border border-indigo-400/30 bg-indigo-400/10 px-2.5 py-2 text-left text-xs text-indigo-100 disabled:opacity-40"
                    >
                      {exclusion.pending ? SESSION_MONITORING_PENDING_COPY : '继续监视此会话'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    role="menuitem"
                    data-testid="session-monitoring-ignore"
                    disabled={monitoringBusy || monitoringUnavailable !== null}
                    onClick={() => {
                      if (monitoringUnavailable !== null) return;
                      setMonitoringError(null);
                      setMenuOpen(false);
                      setConfirmingIgnore(true);
                    }}
                    className="w-full rounded-lg border border-rose-400/25 bg-rose-500/10 px-2.5 py-2 text-left text-xs text-rose-100 disabled:opacity-40"
                  >
                    {SESSION_MONITORING_MENU_LABEL}
                  </button>
                )}
                <p className="mt-1 px-1 text-[10px] leading-relaxed text-slate-500">
                  只停止 daemon 的只读扫描/监视，不删除会话内容，不写 .dsh。
                </p>
                {monitoringUnavailable ? (
                  <p className="mt-1 px-1 text-[10px] leading-relaxed text-amber-200/80">
                    监视命令不可用：{monitoringUnavailable}
                  </p>
                ) : null}
                {monitoringError ? (
                  <p className="mt-1 break-words px-1 text-[10px] text-rose-300">
                    {monitoringError}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-6">
        <div className="flex flex-wrap items-center gap-1.5">
          <SourceBadge source={session.source} />
          <StatusBadge status={session.status} />
          {session.phase ? (
            <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] text-slate-300">
              {session.phase}
            </span>
          ) : null}
          {session.status === 'active' && live ? (
            <span className="text-[10px] text-emerald-300/80">流式刷新中</span>
          ) : null}
        </div>

        <h1 className="mt-2 text-base font-semibold text-slate-100">
          <MaskedText value={session.title} />
        </h1>
        <p className="mt-1 break-all text-[11px] text-slate-500">
          {sessionKey(session).replace('\u0000', ' / ')} · 开始于 {formatClock(startedAt)} · 更新于{' '}
          {formatRelative(session.last_event_at, now)}
        </p>

        <div className="mt-3 grid grid-cols-3 gap-2">
          <Stat label="状态" value={SESSION_STATUS_LABELS[session.status]} />
          <Stat label="进度" value={progress === null ? '—' : `${progress}%`} />
          <Stat label="耗时" value={formatDurationSeconds(duration)} />
          <Stat label="token" value={formatTokens(session.tokens)} />
          <Stat label="花费" value={formatCost(session.cost_cny)} />
          <Stat label="事件" value={String(events.length)} />
        </div>

        {progress !== null || session.phase ? (
          <div className="mt-3 rounded-2xl border border-white/5 bg-white/[0.02] px-3 py-2.5">
            <SessionProgress session={session} />
            {session.model ? (
              <p className="mt-1 text-[10px] text-slate-500">模型 {session.model}</p>
            ) : null}
          </div>
        ) : null}

        <SessionControlPanel session={session} />

        <div className="mt-4 flex items-center justify-between gap-2 text-[11px]">
          <span className="min-w-0 text-slate-500">
            {history.error
              ? '历史加载失败'
              : history.loading
                ? '正在加载更早事件…'
                : history.exhausted
                  ? '已到最早'
                  : '可加载更早历史'}
          </span>
          <button
            type="button"
            data-history-action="load-earlier"
            disabled={history.loading || history.exhausted}
            onClick={() => void loadEarlier()}
            className="shrink-0 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-slate-300 disabled:opacity-40"
          >
            {history.loading
              ? '加载中…'
              : history.exhausted
                ? '已到最早'
                : history.error
                  ? '重试加载更早'
                  : '加载更早'}
          </button>
        </div>
        {history.error ? (
          <p className="mt-1 break-words text-[11px] text-rose-300">{history.error}</p>
        ) : null}

        <div className="mt-4">
          <div className="flex items-center justify-between text-[11px] text-slate-500">
            <h2 className="text-xs font-medium text-slate-300">事件时间线 · {events.length} 条</h2>
            <span>
              {live && session.status === 'active'
                ? `每 2.5s 拉取新事件${lastTailAt ? ` · 最后 ${formatClock(lastTailAt)}` : ''}`
                : '静态快照'}
            </span>
          </div>
          <ol className="mt-2 space-y-2">
            {events.length === 0 ? (
              <li className="rounded-2xl border border-white/10 bg-[#0f1629] p-6 text-center text-xs text-slate-500">
                该会话暂无事件。
              </li>
            ) : (
              events.map((event, index) => (
                <li
                  key={`${eventKey(event)}#${index}`}
                  className="rounded-xl border border-white/5 bg-[#0f1629] px-3 py-2 text-xs"
                >
                  <div className="flex items-center justify-between gap-2 text-[10px] text-slate-500">
                    <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                      <EventKindBadge kind={event.kind} />
                      {event.role ? <span>· {event.role}</span> : null}
                      {event.tool ? <code className="text-sky-200/80">· {event.tool}</code> : null}
                    </span>
                    <span className="shrink-0">{formatClock(event.ts)}</span>
                  </div>
                  {event.text ? (
                    <div className="mt-1 text-slate-200">
                      <MaskedText value={event.text} multiline />
                    </div>
                  ) : null}
                </li>
              ))
            )}
          </ol>
        </div>

        <div className="mt-4">
          <E2ENote compact />
        </div>
      </div>

      <ConfirmDialog
        open={confirmingIgnore}
        title={SESSION_MONITORING_CONFIRM_TITLE}
        danger
        busy={monitoringBusy}
        confirmLabel={SESSION_MONITORING_CONFIRM_ACTION}
        onCancel={() => {
          if (!monitoringBusy) setConfirmingIgnore(false);
        }}
        onConfirm={() => void confirmIgnore()}
      >
        <ul className="list-disc space-y-1.5 pl-4 text-[11px] leading-relaxed text-slate-400">
          {SESSION_MONITORING_CONFIRM_LINES.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        {monitoringError ? (
          <p className="break-words rounded-lg border border-rose-400/30 bg-rose-400/10 px-2.5 py-2 text-[11px] text-rose-200">
            {monitoringError}
          </p>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}
